import React, { useState, useEffect } from "react";
import { Container, Typography, Button, Modal, Box } from "@mui/material";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { encryptData } from "../../utils/cryptoUtils";
import { agent } from "../../utils/agent";
import toggleNetworkRequestStatus from "../../actions/toggleNetworkRequestStatus";

function KmrunHistory() {
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [excelFile, setExcelFile] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const PAGE_SIZE = 50;

  const fetchKmRunHistory = async (page = 1) => {
    try {
      dispatch(toggleNetworkRequestStatus(true));
      const params = encryptData({
        start: (page - 1) * PAGE_SIZE,
        length: PAGE_SIZE,
      });
      const response = await agent.post("get-km-run-history", params);
      dispatch(toggleNetworkRequestStatus(false));
      if (response?.data?.success) {
        setTableData(response.data.data || []);
        setTotalRecords(response.data.total || 0);
      }
    } catch (err) {
      dispatch(toggleNetworkRequestStatus(false));
      console.error(err);
      toast.error("Failed to fetch data");
    }
  };

  useEffect(() => {
    fetchKmRunHistory(currentPage);
  }, [currentPage]);

  const totalPages = Math.ceil(totalRecords / PAGE_SIZE);

  const handleSubmit = async () => {
    if (!excelFile) {
      toast.warning("Please select excel file");
      return;
    }
    try {
      dispatch(toggleNetworkRequestStatus(true));
      const formData = new FormData();
      formData.append("file", excelFile);
      const response = await agent.post("upload-km-run-history", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      dispatch(toggleNetworkRequestStatus(false));
      if (response?.data?.success) {
        toast.success(response.data.message);
        setOpen(false);
        setExcelFile(null);
        setCurrentPage(1);
        fetchKmRunHistory(1);
      } else {
        toast.error(response?.data?.message || "Upload failed");
      }
    } catch (err) {
      dispatch(toggleNetworkRequestStatus(false));
      console.error(err);
      toast.error("Something went wrong");
    }
  };
const formatDateTime = (dateString) => {
  if (!dateString) return "-";

  const date = new Date(dateString);

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = String(date.getFullYear()).slice(-2);

  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");

  return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
};

  return (
    <>
      <Container maxWidth="xl">
        <div className="d-flex align-items-center justify-content-between mb-4">
          <Typography variant="h4">KM Run History</Typography>
          <Button variant="contained" onClick={() => setOpen(true)}>
            + Upload Excel
          </Button>
        </div>

        <div className="table-wrap">
          <table className="table table-hover table-bordered">
            <thead>
              <tr>
                <th>Agreement Number</th>
                <th>Chassis Number</th>
                <th>OEM</th>
                <th>First Timestamp</th>
                <th>Last Timestamp</th>
                <th>Start Odometer</th>
                <th>End Odometer</th>
                <th>Run KM</th>
              </tr>
            </thead>
            <tbody>
              {tableData.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center">No data found</td>
                </tr>
              ) : (
                tableData.map((item, index) => (
                  <tr key={index}>
                    <td>{item.agreement_number}</td>
                    <td>{item.chassis_number}</td>
                    <td>{item.oem}</td>
                    <td>{formatDateTime(item.first_timestamp)}</td>
                    <td>{formatDateTime(item.last_timestamp)}</td>
                    <td>{item.start_odometer}</td>
                    <td>{item.end_odometer}</td>
                    <td>{item.run_km}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="d-flex align-items-center justify-content-between mt-3">
            <small className="text-muted">
              Showing {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, totalRecords)} of {totalRecords} records
            </small>
            <ul className="pagination mb-0">
              <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                <button className="page-link" onClick={() => setCurrentPage(1)}>«</button>
              </li>
              <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                <button className="page-link" onClick={() => setCurrentPage(p => p - 1)}>‹</button>
              </li>

              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2)
                .reduce((acc, p, idx, arr) => {
                  if (idx > 0 && p - arr[idx - 1] > 1) acc.push("...");
                  acc.push(p);
                  return acc;
                }, [])
                .map((p, idx) =>
                  p === "..." ? (
                    <li key={`ellipsis-${idx}`} className="page-item disabled">
                      <span className="page-link">…</span>
                    </li>
                  ) : (
                    <li key={p} className={`page-item ${currentPage === p ? "active" : ""}`}>
                      <button className="page-link" onClick={() => setCurrentPage(p)}>{p}</button>
                    </li>
                  )
                )}

              <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                <button className="page-link" onClick={() => setCurrentPage(p => p + 1)}>›</button>
              </li>
              <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                <button className="page-link" onClick={() => setCurrentPage(totalPages)}>»</button>
              </li>
            </ul>
          </div>
        )}
      </Container>

      <Modal open={open} onClose={() => setOpen(false)}>
        <Box
          sx={{
            width: 500,
            bgcolor: "#fff",
            p: 3,
            borderRadius: 2,
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          }}
        >
          <h4 className="mb-3">Upload Excel File</h4>
          <input
            type="file"
            accept=".xlsx,.xls,.csv"
            className="form-control mb-3"
            onChange={(e) => setExcelFile(e.target.files[0])}
          />
          <div className="d-flex justify-content-end gap-2">
            <Button variant="outlined" onClick={() => setOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleSubmit}>Submit</Button>
          </div>
        </Box>
      </Modal>
    </>
  );
}

export default KmrunHistory;